"use client";

import { useEffect } from "react";

import { scrubPlausiblePayload } from "@/lib/analytics-url";
import { getBaseAnalyticsProps } from "@/lib/analytics-base-props";

let initialized = false;

interface PlausibleAnalyticsProps {
  readonly domain?: string;
  readonly endpoint?: string;
}

export function PlausibleAnalytics({
  domain,
  endpoint,
}: PlausibleAnalyticsProps) {
  useEffect(() => {
    if (!domain || initialized) return;

    initialized = true;
    import("@plausible-analytics/tracker")
      .then(({ init }) => {
        init({
          domain,
          endpoint,
          autoCapturePageviews: true,
          bindToWindow: true,
          // Called per event at track time, so SDK-internal events (auto
          // pageviews, engagement) carry the base dimensions too — exp_<key>
          // and `locale` — not just the goal events that route through
          // trackEvent. Per-event props win on merge, so explicit props
          // (e.g. Experiment Enrolled) are preserved.
          customProperties: () => getBaseAnalyticsProps(),
          // Strip content out of the URL and referrer before they leave the
          // browser — our paths name customers, projects and files
          // (lib/analytics-url.ts). transformRequest is the tracker's single
          // send path, so this covers SDK-internal events as above, and
          // `bindToWindow` means trackEvent's window.plausible calls route
          // through it too. Same scrub PostHog gets in posthog-analytics.tsx.
          transformRequest: scrubPlausiblePayload,
        });
      })
      .catch(() => {
        initialized = false;
      });
  }, [domain, endpoint]);

  return null;
}
